import type { KeycloakLike } from '@/plugins/keycloak'
import type { ReportOf, StatisticsKind, StatisticsPeriod } from '@/types/statistics'
import { periodLabel, periodQuery } from '@/utils/period'
import { ApiError } from './errors'
import type { Me, RewardCoursesReport, RewardTeachersReport, ScheduleLastYear, StatisticsPeriods } from './contract'
import type { ScheduleKey } from '@/types/schedule'
import { authorizedFetch } from './http'

/** GET /api/me: who the caller is and which roles they have. */
export async function fetchMe(keycloak: KeycloakLike, fetchImpl?: typeof fetch): Promise<Me> {
    const response = await authorizedFetch(keycloak, '/api/me', { headers: { Accept: 'application/json' } }, fetchImpl)
    return (await response.json()) as Me
}

/** JSON-RPC statistics.periods: the semesters and academic years with data, newest first. */
export async function fetchStatisticsPeriods(keycloak: KeycloakLike, fetchImpl?: typeof fetch): Promise<StatisticsPeriods> {
    const body = JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'statistics.periods', params: {} })
    const init = { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body }
    const response = await authorizedFetch(keycloak, '/api/rpc', init, fetchImpl)
    const answer = (await response.json()) as { result?: StatisticsPeriods }
    if (!answer.result) {
        throw new ApiError(response.status, 'invalid-response', 'Invalid response', 'statistics.periods returned no result.')
    }
    return answer.result
}

/** JSON-RPC schedule.lastYear: last year's schedule as it would be copied into `key`, or null when there is nothing to copy. */
export async function fetchLastYear(keycloak: KeycloakLike, key: ScheduleKey, fetchImpl?: typeof fetch): Promise<ScheduleLastYear | null> {
    const body = JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'schedule.lastYear', params: { to: key } })
    const init = { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body }
    const response = await authorizedFetch(keycloak, '/api/rpc', init, fetchImpl)
    const answer = (await response.json()) as {
        result?: ScheduleLastYear | null
        error?: { message: string; data?: { code?: string } }
    }
    if (answer.error) {
        const { message, data } = answer.error
        throw new ApiError(response.status, data?.code ?? 'rpc-error', message, message)
    }
    return answer.result ?? null
}

/** JSON-RPC schedule.copyPreview: the schedule `from` as it would be copied into `to`; never null, a refusal is an error. */
export async function fetchCopyPreview(
    keycloak: KeycloakLike,
    from: ScheduleKey,
    to: ScheduleKey,
    fetchImpl?: typeof fetch,
): Promise<ScheduleLastYear> {
    const body = JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'schedule.copyPreview', params: { from, to } })
    const init = { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body }
    const response = await authorizedFetch(keycloak, '/api/rpc', init, fetchImpl)
    const answer = (await response.json()) as {
        result?: ScheduleLastYear
        error?: { message: string; data?: { code?: string } }
    }
    if (answer.error) {
        const { message, data } = answer.error
        throw new ApiError(response.status, data?.code ?? 'rpc-error', message, message)
    }
    if (!answer.result) {
        throw new ApiError(response.status, 'invalid-response', 'Invalid response', 'schedule.copyPreview returned no result.')
    }
    return answer.result
}

/** GET /api/reports/statistics/<kind>?<period>[&username=…]: a statistics report as JSON. */
export async function fetchStatistics<K extends StatisticsKind>(
    keycloak: KeycloakLike,
    kind: K,
    period: StatisticsPeriod,
    username?: string,
    fetchImpl?: typeof fetch,
): Promise<ReportOf[K]> {
    const url = `/api/reports/statistics/${kind}?${periodQuery(period, username)}`
    const response = await authorizedFetch(keycloak, url, { headers: { Accept: 'application/json' } }, fetchImpl)
    return (await response.json()) as ReportOf[K]
}

export type RewardsKind = 'courses' | 'teachers'

export interface RewardsReportOf {
    courses: RewardCoursesReport
    teachers: RewardTeachersReport
}

/** GET /api/reports/rewards/<kind>?semester=…: a rewards report for English teaching as JSON. */
export async function fetchRewards<K extends RewardsKind>(
    keycloak: KeycloakLike,
    kind: K,
    semester: string,
    fetchImpl?: typeof fetch,
): Promise<RewardsReportOf[K]> {
    const url = `/api/reports/rewards/${kind}?${new URLSearchParams({ semester })}`
    const response = await authorizedFetch(keycloak, url, { headers: { Accept: 'application/json' } }, fetchImpl)
    return (await response.json()) as RewardsReportOf[K]
}

/** Every report that downloads as CSV. */
export type CsvKind = StatisticsKind | 'evaluation' | 'rewardCourses' | 'rewardTeachers'

const CSV_PATH: Record<CsvKind, string> = {
    personal: '/api/reports/statistics/personal',
    teachers: '/api/reports/statistics/teachers',
    courses: '/api/reports/statistics/courses',
    evaluation: '/api/reports/evaluation',
    rewardCourses: '/api/reports/rewards/courses',
    rewardTeachers: '/api/reports/rewards/teachers',
}

const CSV_FALLBACK_SUFFIX: Record<CsvKind, string> = {
    personal: 'personal',
    teachers: 'stats',
    courses: 'courses',
    evaluation: 'evaluation',
    rewardCourses: 'rewards-courses',
    rewardTeachers: 'rewards-teachers',
}

/** The reports that exist only for one semester. */
const SEMESTER_ONLY: readonly CsvKind[] = ['evaluation', 'rewardCourses', 'rewardTeachers']

/** The report as a CSV file: the server's Content-Disposition filename, or the legacy name (D106). */
export async function fetchReportCsv(
    keycloak: KeycloakLike,
    kind: CsvKind,
    period: StatisticsPeriod,
    fetchImpl?: typeof fetch,
): Promise<{ blob: Blob; filename: string }> {
    if (SEMESTER_ONLY.includes(kind) && !('semester' in period)) {
        throw new Error(`The ${kind} export needs a semester period.`)
    }
    const response = await authorizedFetch(
        keycloak,
        `${CSV_PATH[kind]}.csv?${periodQuery(period)}`,
        { headers: { Accept: 'text/csv' } },
        fetchImpl,
    )
    if (!(response.headers.get('Content-Type') ?? '').toLowerCase().startsWith('text/csv')) {
        throw new ApiError(response.status, 'unexpected-content', 'Unexpected response', 'The server did not return a CSV file.')
    }
    const named = /(?:^|;)\s*filename=(?:"([^"]+)"|([^";\s]+))/.exec(response.headers.get('Content-Disposition') ?? '')
    return {
        blob: await response.blob(),
        filename: named?.[1] ?? named?.[2] ?? `${periodLabel(period)}-${CSV_FALLBACK_SUFFIX[kind]}.csv`,
    }
}
